// Sentry init must run before any other lexide module so the SDK is
// ready when React mounts. See ai-docs/sentry-integration-guide.md §3.
import "./sentry";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { installGlobalCapture } from "./lib/client-log";
import { initThemeSync } from "./lib/theme";
import i18n, { setUpI18nStoreBridge } from "./i18n";
import { I18nextProvider } from "react-i18next";
import "./index.css";

// Premium-viewer CSS (loaded once at app boot so the lazy-mounted
// viewers don't FOUC on first open). highlight.js theme paints code
// blocks inside MarkdownHtmlViewer; katex paints math; react-pdf
// supplies the text + annotation layer styling pdf.js needs to
// position selection + clickable links over the canvas page.
import "highlight.js/styles/github-dark.css";
import "katex/dist/katex.min.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Phase 7.5: apply the stored light/dark/system preference before
// first paint, then keep the `<html>` class in sync with OS-pref
// changes for users on "system" mode. Subscription lives for the
// page's lifetime — no teardown needed.
initThemeSync();

// Phase A i18n: wire `useLanguageStore` → i18next. Calling this once at
// startup means any subsequent `setLang(...)` (settings UI, auth-exchange
// hydration) flips the active translation namespace. Idempotent.
setUpI18nStoreBridge();

// Wire window.onerror + unhandledrejection → operationalLogs collection
// via /api/client-log so platform admins see SPA-side errors that
// otherwise live only in the user's browser console. See
// ai-docs/operational-logging-guide.md §"SPA client-log channel".
installGlobalCapture();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <App />
    </I18nextProvider>
  </StrictMode>,
);
